import { permissionsQueryKeys } from '@features/permissions/api/queryKeys'
import { settingsQueryKeys } from '@features/settings/api/queryKeys'
import { useMicrosoftConnectionStatus } from '@features/integrations/hooks/useMicrosoftConnectionStatus'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createAppNavigation } from '@/config/appNavigation'
import { NAVIGATION_QUERY_STALE_TIME_MS } from '@/constants'
import { getEnabledModuleSlugs, useSettingsStore } from '@features/settings/stores/useSettingsStore'
import { getModules } from '@features/permissions/api/permissions.api'
import { getModulePermissionKey } from '@features/modules/logic/moduleAccess'
import { getSettings } from '@features/settings/api/settings.api'

/**
 * The navigation modules the current user may open: the tenant's enabled
 * modules, each behind the permission module the catalog names for it (none
 * for the AI Assistant), checked against GET /permissions/modules. Settings
 * pages are only requested for users who have the Settings module.
 */
export function useAppNavigation() {
  const modulesQuery = useQuery({
    queryKey: permissionsQueryKeys.permissionsModules,
    queryFn: getModules,
  })
  const permittedModuleKeys = useMemo(
    () => (modulesQuery.data ? new Set(modulesQuery.data.map((module) => module.key)) : null),
    [modulesQuery.data]
  )
  const canViewSettings = permittedModuleKeys?.has('settings') ?? false
  const settingsQuery = useQuery({
    queryKey: settingsQueryKeys.settings,
    queryFn: getSettings,
    enabled: canViewSettings,
    staleTime: NAVIGATION_QUERY_STALE_TIME_MS,
  })
  const resolvedModules = useSettingsStore((state) => state.modules)
  // Credentials only appears where Microsoft 365 connections are configured,
  // which doesn't change in a session: read as the other navigation lists are.
  const microsoftConnection = useMicrosoftConnectionStatus({
    enabled: canViewSettings,
    staleTime: NAVIGATION_QUERY_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })
  const credentialsAvailable = microsoftConnection.data?.configured === true

  const enabledModuleSlugs = useMemo(
    () => getEnabledModuleSlugs(resolvedModules),
    [resolvedModules]
  )

  // A module anyone may use (or the account area) needs no permission.
  const modules = useMemo(
    () =>
      createAppNavigation(settingsQuery.data?.pages ?? [], enabledModuleSlugs, {
        credentialsAvailable,
      }).filter((module) => {
        const permissionKey = getModulePermissionKey(resolvedModules, module.moduleSlug)
        return permissionKey === null || permittedModuleKeys?.has(permissionKey) === true
      }),
    [
      settingsQuery.data?.pages,
      enabledModuleSlugs,
      credentialsAvailable,
      permittedModuleKeys,
      resolvedModules,
    ]
  )

  return {
    modules,
    /**
     * Which modules the user may open isn't known yet: areas behind a
     * permission are missing from `modules` until this settles.
     */
    modulesPending: modulesQuery.isPending,
    /**
     * Which modules the user may open couldn't be read (the request failed,
     * with nothing from before): areas behind a permission are missing from
     * `modules`, though they may be open to the user. Unknown, not taken away.
     */
    modulesError: modulesQuery.isError && modulesQuery.data === undefined,
    settingsNavigationPending: canViewSettings && settingsQuery.isPending,
    settingsNavigationError: canViewSettings && settingsQuery.isError,
  }
}
